import React, { useState, useEffect } from 'react';
import { Lock, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let cachedLocks = null;
let cacheTime = 0;
const CACHE_TTL = 10000;

export function usePageLock(pageKey) {
  const [lockState, setLockState] = useState({ loading: true, locked: false, title: '', message: '' });

  useEffect(() => {
    const fetchLocks = async () => {
      const now = Date.now();
      if (cachedLocks && now - cacheTime < CACHE_TTL) {
        const lock = cachedLocks[pageKey];
        setLockState({ loading: false, locked: !!lock?.locked, title: lock?.title || '', message: lock?.message || '' });
        return;
      }
      try {
        const res = await axios.get(`${BACKEND_URL}/api/page-locks`);
        cachedLocks = res.data;
        cacheTime = now;
        const lock = res.data[pageKey];
        setLockState({ loading: false, locked: !!lock?.locked, title: lock?.title || '', message: lock?.message || '' });
      } catch {
        setLockState({ loading: false, locked: false, title: '', message: '' });
      }
    };
    fetchLocks();
    const interval = setInterval(fetchLocks, CACHE_TTL);
    return () => clearInterval(interval);
  }, [pageKey]);

  return lockState;
}

export function PageLockGate({ pageKey, children }) {
  const { loading, locked, title, message } = usePageLock(pageKey);
  const navigate = useNavigate();

  const handleClose = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (locked) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" data-testid="page-lock-overlay">
        <button
          onClick={handleClose}
          data-testid="page-lock-close"
          className="absolute top-6 right-6 text-zinc-500 hover:text-white transition-colors"
        >
          <X size={28} />
        </button>
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-zinc-800 border border-zinc-700 flex items-center justify-center">
            <Lock className="text-primary" size={40} />
          </div>
          <h2 className="font-heading font-bold text-3xl text-white mb-3" data-testid="page-lock-title">
            {title || 'Page Locked'}
          </h2>
          {message && (
            <p className="text-zinc-300 text-lg leading-relaxed whitespace-pre-wrap" data-testid="page-lock-message">
              {message}
            </p>
          )}
          <button
            onClick={handleClose}
            data-testid="page-lock-back-btn"
            className="mt-8 px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-bold text-sm transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return children;
}

export default PageLockGate;
